import React from "react";
import { AlertTriangle, Check, Search, Sparkles, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtNOK, fmtCompact, initials as mkInitials } from "@/lib/format";
import { stageColor, stageLabel } from "@/lib/stages";
import { Button } from "@/components/ui/button";

export function StageBadge({ stage, stages, className, testId }) {
  return (
    <span data-testid={testId || `stage-badge-${stage}`} className={cn("inline-flex items-center gap-1.5 rounded-full border border-black/[0.08] bg-white px-2.5 py-1 text-[11.5px] font-medium text-foreground/85 shadow-[0_1px_1px_hsl(212_40%_16%/0.03)]", className)}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: stageColor(stage) }} />
      {stageLabel(stage, stages)}
    </span>
  );
}

export function HealthIndicator({ health, reason, compact = false }) {
  if (!health || health === "closed") return null;
  const bad = health === "needs_action";
  return (
    <span title={reason || ""} data-testid={`health-${health}`} className={cn("inline-flex items-center gap-1.5 text-xs font-medium", bad ? "text-[hsl(var(--status-warn))]" : "text-[hsl(var(--status-ok))]")}>
      <span className={cn("h-1.5 w-1.5 rounded-full", bad ? "bg-[hsl(var(--status-warn))] pulse-dot" : "bg-[hsl(var(--status-ok))]")} />
      {!compact && (bad ? "Needs action" : "On track")}
    </span>
  );
}

const STATUS_META = {
  covered: { label: "Covered", Icon: Check, cls: "text-[hsl(var(--status-ok))] bg-[hsl(var(--status-ok)/0.08)] border-[hsl(var(--status-ok)/0.25)]" },
  needs_review: { label: "Needs review", Icon: AlertTriangle, cls: "text-[hsl(var(--status-warn))] bg-[hsl(var(--status-warn)/0.08)] border-[hsl(var(--status-warn)/0.3)]" },
  not_covered: { label: "Not covered", Icon: X, cls: "text-[hsl(var(--status-bad))] bg-[hsl(var(--status-bad)/0.08)] border-[hsl(var(--status-bad)/0.3)]" },
  identified: { label: "Identified", Icon: Check, cls: "text-[hsl(var(--status-ok))] bg-[hsl(var(--status-ok)/0.08)] border-[hsl(var(--status-ok)/0.25)]" },
  needs_clarification: { label: "Needs clarification", Icon: AlertTriangle, cls: "text-[hsl(var(--status-warn))] bg-[hsl(var(--status-warn)/0.08)] border-[hsl(var(--status-warn)/0.3)]" },
  resolved: { label: "Resolved", Icon: Check, cls: "text-muted-foreground bg-muted border-border" },
  draft: { label: "Draft", Icon: null, cls: "text-muted-foreground bg-muted border-border" },
  sent: { label: "Sent", Icon: null, cls: "text-primary bg-primary/10 border-primary/25" },
  superseded: { label: "Superseded", Icon: null, cls: "text-muted-foreground bg-white border-border" },
  accepted: { label: "Accepted", Icon: Check, cls: "text-[hsl(var(--status-ok))] bg-[hsl(var(--status-ok)/0.08)] border-[hsl(var(--status-ok)/0.25)]" },
  ready: { label: "Ready", Icon: Check, cls: "text-[hsl(var(--status-ok))] bg-[hsl(var(--status-ok)/0.08)] border-[hsl(var(--status-ok)/0.25)]" },
  extracting: { label: "Extracting", Icon: null, cls: "text-primary bg-primary/10 border-primary/25" },
  analyzing: { label: "Analysing", Icon: null, cls: "text-primary bg-primary/10 border-primary/25" },
  matching: { label: "Matching", Icon: null, cls: "text-primary bg-primary/10 border-primary/25" },
  failed: { label: "Failed", Icon: X, cls: "text-[hsl(var(--status-bad))] bg-[hsl(var(--status-bad)/0.08)] border-[hsl(var(--status-bad)/0.3)]" },
  must: { label: "Must", Icon: null, cls: "text-foreground bg-white border-border font-semibold" },
  should: { label: "Should", Icon: null, cls: "text-muted-foreground bg-white border-border" },
  nice: { label: "Nice", Icon: null, cls: "text-muted-foreground bg-white border-border" },
  info: { label: "Info", Icon: null, cls: "text-muted-foreground bg-muted border-border" },
};

export function StatusChip({ status, label, className, testId }) {
  const m = STATUS_META[status] || { label: label || status, Icon: null, cls: "text-muted-foreground bg-muted border-border" };
  const Icon = m.Icon;
  return (
    <span data-testid={testId || `status-chip-${status}`} className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap", m.cls, className)}>
      {Icon ? <Icon className="h-3 w-3" /> : null}
      {label || m.label}
    </span>
  );
}

export function Money({ value, compact = false, currency = "NOK", className, testId }) {
  return (
    <span data-testid={testId} className={cn("tabular font-medium", className)}>
      {compact ? fmtCompact(value, currency) : `${fmtNOK(value)} ${currency}`}
    </span>
  );
}

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
/** Avatars are stored behind the API; relative «/api/…» urls are resolved against the backend so <img> works everywhere. */
export const avatarSrc = (src) => (src && src.startsWith("/api/") ? `${BACKEND}${src}` : src);

export function UserAvatar({ name, initials, color, src, size = "sm", className, testId }) {
  const [broken, setBroken] = React.useState(false);
  const s = size === "xl" ? "h-14 w-14 text-base" : size === "lg" ? "h-10 w-10 text-sm" : size === "md" ? "h-8 w-8 text-xs" : size === "xs" ? "h-5 w-5 text-[9px]" : "h-6 w-6 text-[10px]";
  const url = avatarSrc(src);
  React.useEffect(() => { setBroken(false); }, [url]);
  if (url && !broken) {
    return (
      <span data-testid={testId} title={name} className={cn("inline-flex shrink-0 overflow-hidden rounded-full ring-1 ring-black/[0.08]", s, className)}>
        <img src={url} alt={name || ""} className="h-full w-full object-cover" onError={() => setBroken(true)} draggable={false} />
      </span>
    );
  }
  return (
    <span data-testid={testId} title={name} className={cn("inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white", s, className)} style={{ backgroundColor: color || "#111" }}>
      {initials || mkInitials(name)}
    </span>
  );
}

/** Manufacturer brand mark (favicon) with monogram fallback. */
export function BrandMark({ name, domain, size = "sm", className }) {
  const [broken, setBroken] = React.useState(false);
  const d = cleanDomain(domain);
  const s = size === "lg" ? "h-9 w-9 rounded-lg p-1.5" : size === "md" ? "h-7 w-7 rounded-md p-1" : "h-5 w-5 rounded p-0.5";
  if (!d || broken) return <Monogram name={name} size="sm" className={cn(size === "sm" && "h-5 w-5 rounded text-[8px]", size === "md" && "h-7 w-7 rounded-md text-[9px]", size === "lg" && "h-9 w-9 rounded-lg text-[10px]", className)} />;
  return (
    <span className={cn("inline-flex shrink-0 items-center justify-center overflow-hidden border border-black/[0.06] bg-white", s, className)} title={name}>
      <img src={`https://www.google.com/s2/favicons?sz=128&domain=${d}`} alt="" className="h-full w-full object-contain" onError={() => setBroken(true)} loading="lazy" draggable={false} />
    </span>
  );
}

const CATEGORY_ICON_LABEL = {
  "PTZ Camera": "PTZ",
  Camera: "CAM",
  Lens: "LENS",
  Control: "CTRL",
  Switcher: "SW",
  Recording: "REC",
  "Routing & IP": "IP",
  Streaming: "LIVE",
  Monitoring: "MON",
  Software: "SW",
  Audio: "AUD",
  "Wireless Audio": "RF",
  Intercom: "COM",
  Networking: "NET",
  Graphics: "GFX",
  Lighting: "LUX",
  Support: "KIT",
  Service: "SVC",
};

/**
 * Product image tile. Shows the verified product photo when available; otherwise a calm brand tile
 * (manufacturer mark + category glyph) so line items never look broken or generic.
 * ratio: "square" | "wide" | "video"
 */
export function ProductImage({ item, ratio = "square", size, className, imgClassName, testId, fit = "cover" }) {
  const [broken, setBroken] = React.useState(false);
  const src = item?.image_url;
  const r = ratio === "wide" ? "aspect-[4/3]" : ratio === "video" ? "aspect-[16/10]" : "aspect-square";
  const base = cn("relative flex shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-black/[0.06] bg-[hsl(210_20%_96%)]", size ? "" : r, size, className);
  if (src && !broken) {
    const contain = fit === "contain" || (fit === "auto" && /\.png(\?|$)/i.test(src));
    return (
      <span data-testid={testId} className={base}>
        <img src={src} alt={item?.name || ""} className={cn("h-full w-full", contain ? "object-contain p-[7%]" : "object-cover", imgClassName)} onError={() => setBroken(true)} loading="lazy" draggable={false} />
      </span>
    );
  }
  const glyph = CATEGORY_ICON_LABEL[item?.category] || (item?.is_service ? "SVC" : "KIT");
  return (
    <span data-testid={testId} className={cn(base, "flex flex-col items-center justify-center gap-1 text-foreground/45")}>
      <BrandMark name={item?.manufacturer} domain={item?.manufacturer_domain} size="md" className="border-0 bg-transparent p-0 opacity-90" />
      <span className="text-[9px] font-semibold tracking-[0.14em]">{glyph}</span>
    </span>
  );
}

export function PageHeader({ title, description, actions, className, children }) {
  return (
    <div className={cn("mb-7 flex flex-col gap-4 md:flex-row md:items-end md:justify-between", className)}>
      <div className="min-w-0">
        <h1 className="font-display text-[26px] font-bold leading-[1.1] tracking-[-0.03em] text-[hsl(var(--navy))] md:text-[30px]">{title}</h1>
        {description ? <p className="mt-1.5 max-w-2xl text-[14px] leading-relaxed text-muted-foreground">{description}</p> : null}
        {children}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function SectionTitle({ children, right, className }) {
  return (
    <div className={cn("mb-3 flex items-center justify-between gap-3", className)}>
      <h2 className="text-sm font-semibold tracking-[-0.01em] text-foreground">{children}</h2>
      {right}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action, secondary, className, testId }) {
  return (
    <div data-testid={testId} className={cn("flex flex-col items-center justify-center rounded-2xl border border-dashed border-black/[0.12] bg-white px-6 py-14 text-center", className)}>
      {Icon ? (
        <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-black/[0.06] bg-[hsl(var(--muted))] text-muted-foreground">
          <Icon className="h-5 w-5" />
        </div>
      ) : null}
      <h3 className="text-[15px] font-semibold tracking-[-0.01em]">{title}</h3>
      {description ? <p className="mt-1 max-w-sm text-[13px] leading-relaxed text-muted-foreground">{description}</p> : null}
      {(action || secondary) && (
        <div className="mt-5 flex items-center gap-2">
          {action}
          {secondary}
        </div>
      )}
    </div>
  );
}

export function AIBanner({ children, action, className, testId }) {
  return (
    <div data-testid={testId} className={cn("ai-banner flex items-start gap-3", className)}>
      <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <div className="min-w-0 flex-1 text-sm">{children}</div>
      {action}
    </div>
  );
}

export function AIButton({ children, loading, className, ...props }) {
  return (
    <Button {...props} disabled={loading || props.disabled} className={cn("gap-2", className)}>
      <Sparkles className={cn("h-4 w-4", loading && "animate-pulse")} />
      {loading ? "Working..." : children}
    </Button>
  );
}

export function Stat({ label, value, sub, className, testId }) {
  return (
    <div data-testid={testId} className={cn("min-w-0", className)}>
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular tracking-[-0.02em]">{value}</div>
      {sub ? <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

export function Spinner({ className }) {
  return <span className={cn("inline-block h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-primary", className)} />;
}

export function Skeleton({ className }) {
  return <div className={cn("shimmer rounded-lg", className)} />;
}

export function KeyValue({ label, children, className }) {
  return (
    <div className={cn("flex items-start justify-between gap-4 py-2 text-sm", className)}>
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right font-medium">{children}</span>
    </div>
  );
}

export function Card({ className, children, ...props }) {
  return (
    <div {...props} className={cn("surface", className)}>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Shared premium layout blocks                                         */
/* ------------------------------------------------------------------ */

export function PageContainer({ className, children, wide = false, ...props }) {
  return (
    <div {...props} className={cn("mx-auto px-5 py-8 sm:px-8 lg:px-10 lg:py-10", wide ? "max-w-[1440px]" : "max-w-[1240px]", className)}>
      {children}
    </div>
  );
}

export function monogram(name = "") {
  const words = String(name).trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 3);
  return words.slice(0, 2).map((w) => w[0]).join("");
}

/* Brand-like marks for companies without a fetchable logo: deep, muted tones (never raw colours), white initials. */
const MARK_TONES = ["210 100% 16%", "181 70% 26%", "262 26% 42%", "20 55% 46%", "150 28% 32%", "200 48% 34%", "35 45% 40%", "340 32% 40%", "212 14% 30%"];

const markSize = (size) => (size === "xl" ? "h-16 w-16 rounded-2xl text-[17px]" : size === "lg" ? "h-12 w-12 rounded-xl text-[13px]" : size === "sm" ? "h-8 w-8 rounded-lg text-[10.5px]" : "h-10 w-10 rounded-xl text-[11.5px]");

export function Monogram({ name, size = "md", className, tone = true, testId }) {
  const idx = Math.abs(String(name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0)) % MARK_TONES.length;
  return (
    <span
      data-testid={testId}
      className={cn("flex shrink-0 select-none items-center justify-center border border-black/[0.06] font-semibold uppercase leading-none tracking-wide text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.14)]", markSize(size), className)}
      style={{ backgroundColor: `hsl(${tone ? MARK_TONES[idx] : "225 12% 28%"})` }}
      title={name}
    >
      {monogram(name)}
    </span>
  );
}

export function cleanDomain(website) {
  return String(website || "").trim().replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/[/?#].*$/, "").toLowerCase();
}

/* Logos we host ourselves (public/logos) for customers whose sites only expose a tiny favicon. */

/** Company logo: curated asset in /public/logos/<domain>.png, else Google's favicon service (≤128 px), else a brand mark.
 *  The favicon service answers a 16 px placeholder when a site has no icon, so anything that small counts as "no logo". */
export function CompanyLogo({ name, domain, size = "md", className, testId }) {
  const d = cleanDomain(domain);
  const [step, setStep] = React.useState(0); // 0 local · 1 favicon · 2 failed
  const [ok, setOk] = React.useState(false);
  React.useEffect(() => { setStep(0); setOk(false); }, [d]);
  if (!d || step > 1) return <Monogram name={name} size={size} className={className} testId={testId} />;
  const s = size === "xl" ? "h-16 w-16 rounded-2xl p-2.5" : size === "lg" ? "h-12 w-12 rounded-xl p-2" : size === "sm" ? "h-8 w-8 rounded-lg p-1.5" : "h-10 w-10 rounded-xl p-2";
  const src = step === 0 ? `/logos/${d}.png` : `https://www.google.com/s2/favicons?sz=128&domain=${encodeURIComponent(d)}`;
  return (
    <span data-testid={testId} className={cn("relative flex shrink-0 items-center justify-center overflow-hidden", s, ok ? "border border-black/[0.07] bg-white" : "p-0", className)} title={name}>
      {!ok && <Monogram name={name} size={size} className="absolute inset-0 h-full w-full" />}
      <img
        key={src}
        src={src}
        alt=""
        className={cn("relative h-full w-full object-contain transition-opacity duration-200", ok ? "opacity-100" : "opacity-0")}
        onError={() => setStep((v) => v + 1)}
        onLoad={(e) => (e.currentTarget.naturalWidth <= 16 ? setStep((v) => v + 1) : setOk(true))}
        loading="lazy"
        draggable={false}
      />
    </span>
  );
}

export function Kpi({ label, value, sub, accent, testId, className }) {
  return (
    <div className={cn("px-4 py-5 sm:px-6", className)} data-testid={testId}>
      <div className="eyebrow">{label}</div>
      <div className={cn("font-display mt-2 whitespace-nowrap text-[24px] font-semibold leading-none tabular tracking-[-0.03em] sm:text-[30px]", accent && "text-primary")} data-num="">{value}</div>
      {sub ? <div className="mt-2 text-[12.5px] text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

/** Surface with 2 / n KPI cells and hairline dividers. Pass `cols` = number of KPIs (2-6). */
export function KpiStrip({ children, cols = 4, className, testId }) {
  const lgCols = { 2: "lg:grid-cols-2", 3: "lg:grid-cols-3", 4: "lg:grid-cols-4", 5: "lg:grid-cols-5", 6: "lg:grid-cols-6" }[cols] || "lg:grid-cols-4";
  return (
    <div data-testid={testId} className={cn("surface overflow-hidden", className)}>
      <div className={cn("grid grid-cols-2 [&>*]:border-black/[0.06] [&>*:nth-child(even)]:border-l [&>*:nth-child(n+3)]:border-t lg:[&>*]:border-t-0 lg:[&>*:not(:first-child)]:border-l", lgCols)}>{children}</div>
    </div>
  );
}

/** Segmented pill control for filters/tabs. items: [{key,label,count}] */
export function Segmented({ items, value, onChange, className, testIdPrefix = "segment" }) {
  return (
    <div role="tablist" className={cn("inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-xl border border-black/[0.06] bg-[hsl(210_20%_95%)] p-1", className)}>
      {items.map((it) => {
        const active = it.key === value;
        return (
          <button
            key={it.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(it.key)}
            data-testid={`${testIdPrefix}-${it.key}`}
            className={cn(
              "inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-[9px] px-3 text-[13px] font-medium text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40",
              active && "bg-white text-foreground shadow-[0_1px_2px_hsl(212_40%_16%/0.08),0_0_0_1px_hsl(212_40%_16%/0.04)]"
            )}
          >
            {it.label}
            {it.count !== undefined && it.count !== null ? <span className={cn("tabular text-[11px]", active ? "text-muted-foreground" : "text-muted-foreground/70")}>{it.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

export function SearchField({ value, onChange, placeholder, className, inputClassName, testId, autoFocus }) {
  return (
    <div className={cn("relative", className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/80" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        data-testid={testId}
        className={cn("h-10 w-full rounded-xl border border-black/[0.09] bg-white pl-9 pr-3 text-[14px] shadow-[0_1px_2px_hsl(212_40%_16%/0.04)] placeholder:text-muted-foreground/70 hover:border-black/[0.16] focus-visible:border-primary/60 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/20", inputClassName)}
      />
    </div>
  );
}

export function InlineError({ children, onRetry, testId }) {
  return (
    <div className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-destructive/30 bg-destructive/[0.05] px-4 py-3 text-sm text-destructive" data-testid={testId}>
      <span>{children}</span>
      {onRetry ? <Button size="sm" variant="outline" onClick={onRetry}>Retry</Button> : null}
    </div>
  );
}

/** Renders light markdown (bold, bullets, links) produced by the assistant. */
export function LightMarkdown({ text, className, LinkComponent }) {
  const lines = (text || "").split("\n");
  const render = (s, i) => {
    const parts = [];
    const re = /(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^)]+\))/g;
    let last = 0;
    let m;
    let k = 0;
    while ((m = re.exec(s))) {
      if (m.index > last) parts.push(s.slice(last, m.index));
      if (m[1]) parts.push(<strong key={`${i}-${k++}`}>{m[1].slice(2, -2)}</strong>);
      else if (m[2]) {
        const mm = m[2].match(/\[([^\]]+)\]\(([^)]+)\)/);
        if (mm) {
          const href = mm[2];
          if (LinkComponent && href.startsWith("/")) parts.push(<LinkComponent key={`${i}-${k++}`} to={href} className="text-primary underline underline-offset-2">{mm[1]}</LinkComponent>);
          else parts.push(<a key={`${i}-${k++}`} href={href} className="text-primary underline underline-offset-2" target="_blank" rel="noreferrer">{mm[1]}</a>);
        }
      }
      last = m.index + m[0].length;
    }
    if (last < s.length) parts.push(s.slice(last));
    return parts;
  };
  const out = [];
  let list = [];
  const flush = () => {
    if (list.length) {
      out.push(<ul key={`ul-${out.length}`} className="my-1.5 list-disc space-y-1 pl-5">{list}</ul>);
      list = [];
    }
  };
  lines.forEach((ln, i) => {
    const t = ln.trim();
    if (/^[-*•]\s+/.test(t)) {
      list.push(<li key={i}>{render(t.replace(/^[-*•]\s+/, ""), i)}</li>);
    } else if (/^\d+\.\s+/.test(t)) {
      list.push(<li key={i} className="list-decimal">{render(t.replace(/^\d+\.\s+/, ""), i)}</li>);
    } else {
      flush();
      if (/^#{1,3}\s/.test(t)) out.push(<div key={i} className="mt-2 font-semibold">{render(t.replace(/^#{1,3}\s/, ""), i)}</div>);
      else if (t) out.push(<p key={i} className="my-1">{render(t, i)}</p>);
    }
  });
  flush();
  return <div className={cn("text-sm leading-6", className)}>{out}</div>;
}
